import { defineMessages, type IntlShape } from 'react-intl'
import { isFlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'

const AIRLINE_CODE_PATTERN = /^[A-Z0-9]{2}$/
const FULL_FLIGHT_CODE_PATTERN = /^([A-Z]{2,3}|[A-Z][0-9])\s*-?\s*(\d+[A-Z]?)$/i
const NUMERIC_FLIGHT_NUMBER_PATTERN = /^\d+[A-Z]?$/i

const flightRouteMessages = defineMessages({
  oneWay: {
    id: '_kY6UeGYXGJqKDRJoDZQ',
    defaultMessage: '{departureAirport} → {arrivalAirport}',
    description: 'One-way flight route in the trip planner, from the departure airport to the arrival airport, shown as a flight title or location',
  },
  return: {
    id: '83aB6n-TMYty-ACaZly3',
    defaultMessage: '{departureAirport} ⇄ {arrivalAirport}',
    description: 'Return flight route in the trip planner between the departure airport and the arrival airport, shown as a flight title or location',
  },
})

function getStringProperty(value: object | undefined, property: string): string | undefined {
  if (!value || !(property in value)) return undefined
  const propertyValue = (value as Record<string, unknown>)[property]
  return typeof propertyValue === 'string' ? propertyValue : undefined
}

function getPrimaryFlightNumber(flightNumber: string | undefined): string | undefined {
  return flightNumber?.split('/').map(part => part.trim()).find(Boolean)
}

function formatFlightCode(savedItemData: object | undefined): string | undefined {
  const flightNumber = getPrimaryFlightNumber(getStringProperty(savedItemData, 'flightNumber'))
  if (!flightNumber) return undefined

  const fullCodeMatch = flightNumber.match(FULL_FLIGHT_CODE_PATTERN)
  if (fullCodeMatch) {
    return `${fullCodeMatch[1].toUpperCase()}${fullCodeMatch[2].toUpperCase()}`
  }

  const airlineCode = getStringProperty(savedItemData, 'airlineCode')?.trim().toUpperCase()
  if (airlineCode && AIRLINE_CODE_PATTERN.test(airlineCode) && NUMERIC_FLIGHT_NUMBER_PATTERN.test(flightNumber)) {
    return `${airlineCode}${flightNumber.toUpperCase()}`
  }

  return undefined
}

function getFlightCodes(option: TransportOption): string | undefined {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnSavedItemData = isFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined
  return [
    formatFlightCode(savedItemData),
    formatFlightCode(returnSavedItemData),
  ].filter(Boolean).join(' / ') || undefined
}

export function formatFlightRoute(
  intl: IntlShape,
  option: TransportOption,
  departureAirport: string,
  arrivalAirport: string,
): string {
  return intl.formatMessage(option.returnLeg ? flightRouteMessages.return : flightRouteMessages.oneWay, { departureAirport, arrivalAirport })
}

function getFlightRoute(intl: IntlShape, option: TransportOption): string | undefined {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  if (!savedItemData?.departureAirport || !savedItemData.arrivalAirport) return undefined

  return formatFlightRoute(intl, option, savedItemData.departureAirport, savedItemData.arrivalAirport)
}

export function buildFlightName(intl: IntlShape, option: TransportOption, fallback: string): string {
  const saved = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  if (!saved) return option.label || fallback

  const title = [getFlightRoute(intl, option), getFlightCodes(option)].filter(Boolean).join(' · ')
  if (title) return title

  return getPrimaryFlightNumber(getStringProperty(saved, 'flightNumber')) ?? (option.label || fallback)
}
